import type { PropsWithChildren, FC } from 'react';

import { arrowClass, errorContainerClass } from './InputError.css';
import { getClassNames } from '../../util/css';
import { Text } from '../Text/Text';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { visuallyHiddenClass } from '../VisuallyHidden/VisuallyHidden.css';

interface Props {
  id?: string;
  children: string;
  hasArrow?: boolean;
  /** Optional additional CSS class */
  className?: string;
}

/** Arrow indicator for error message (roughly 12×6px) */
export const Arrow: FC = () => <div className={arrowClass} />;

Arrow.displayName = 'Arrow';

/**
 * Internal use only (InputText, InputTelephone, etc.)
 *
 * Always render this component, passing an empty string when there is no error, rather than
 * mounting/unmounting it based on error presence. A screen reader reliably announces a live
 * region's text *changing*, but generally does not announce a brand new node mounting with its
 * text already set - so a conditionally rendered `role="status"` region can miss the very first
 * error entirely. Keeping it mounted and toggling only its visibility (still in the render tree,
 * not display:none) keeps a single source of truth for the message while fixing that gap.
 */
export const InputError: FC<PropsWithChildren<Props>> = ({
  id,
  children,
  hasArrow = true,
  className,
}) => {
  const hasError = !!children;
  const endsWithPunctuation = /[.?!]$/.test(children);

  return (
    <div className={getClassNames(className, !hasError && visuallyHiddenClass)}>
      {hasArrow && <Arrow />}
      <div className={errorContainerClass} id={id} role="status">
        <Text color="textInverted" size="BodyS">
          {children}
          {hasError && !endsWithPunctuation ? <VisuallyHidden>{'.'}</VisuallyHidden> : null}
        </Text>
      </div>
    </div>
  );
};
